import _ from 'lodash'
import {
  Box,
  Button,
  darken,
  Grid,
  makeStyles,
  Paper,
  Typography,
} from '@material-ui/core'
import React, { useState } from 'react'
import { Plus } from 'react-feather'
import { useTranslation } from 'react-i18next'
import { useEnergySystems } from '../../../energy-system/use-energy-systems'
import {
  BuildingEnergyModelFragment,
  CreateEnergySystemInput,
  EnergySystemFragment,
  EnergySystemType,
} from '../../../generated/graphql'
import { LoadingDialog } from '../../common/LoadingDialog'
import { EnergySystemDialog } from './EnergySystemDialog'
import {
  convertInputParametersToFormParameters,
  defaultValuesByType,
  FormValues,
  mapFormValuesToInput,
} from './form-values'
import { theme } from '../../../theme'

const useStyles = makeStyles((themeSpec) => ({
  energySystem: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: themeSpec.spacing(3),
    height: 170,
    width: 300,
    cursor: 'pointer',

    '&:hover': {
      backgroundColor: darken(themeSpec.palette.background.paper, 0.02),
    },
  },
  energySystemPropertyName: {
    textTransform: 'none',
  },
  energySystemPropertyValue: {
    color: themeSpec.palette.grey[500],
  },
}))

const energySystemTypeOrder: Record<EnergySystemType, number> = {
  DomesticHotWater: 0,
  Ventilation: 3,
  Cooling: 4,
  HeatLoad: 2,
  ElectricityConsumption: 5,
  SpaceHeating: 1,
}

type Props = {
  buildingEnergyModel: BuildingEnergyModelFragment
}

export function EnergySystemList({
  buildingEnergyModel,
}: Props): React.ReactElement {
  const { t } = useTranslation()
  const classes = useStyles()
  const [isCreatingEnergySystem, setIsCreatingEnergySystem] = useState(false)
  const [editingEnergySystem, setEditingEnergySystem] = useState<
    EnergySystemFragment | undefined
  >()
  const {
    createEnergySystem,
    isCreateLoading,
    updateEnergySystem,
    isUpdateLoading,
    deleteEnergySystem,
    isDeleteLoading,
  } = useEnergySystems()
  const { energySystems } = buildingEnergyModel

  const onDialogClose = () => {
    setIsCreatingEnergySystem(false)
    setEditingEnergySystem(undefined)
  }

  const onCreateStart = () => setIsCreatingEnergySystem(true)

  const onCreateSubmit = async (values: FormValues) => {
    const input = mapFormValuesToInput({
      ...values,
      modelArea: buildingEnergyModel.area,
    })
    await createEnergySystem(
      buildingEnergyModel,
      input as CreateEnergySystemInput,
    )
    setIsCreatingEnergySystem(false)
  }

  const onEditSubmit = async (values: FormValues) => {
    if (!editingEnergySystem) {
      return
    }
    const input = mapFormValuesToInput({
      ...values,
      modelArea: buildingEnergyModel.area,
    })
    await updateEnergySystem(editingEnergySystem.id, input)
    setEditingEnergySystem(undefined)
  }

  const onDelete = async () => {
    if (!editingEnergySystem) {
      return
    }
    setEditingEnergySystem(undefined)
    await deleteEnergySystem(buildingEnergyModel, editingEnergySystem.id)
  }

  const allowedEnergySystems = _.difference(
    Object.values(EnergySystemType),
    buildingEnergyModel.energySystems.map((es) => es.type),
  ).sort((a, b) => energySystemTypeOrder[a] - energySystemTypeOrder[b])

  return (
    <>
      <Box marginY={theme.spacing(1)}>
        <Box>
          <Box
            mb={2}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h5">
              {t('buildingEnergyModels.drawer.energySystems')}
            </Typography>

            <Button
              variant="contained"
              color="primary"
              startIcon={<Plus />}
              onClick={onCreateStart}
              disabled={allowedEnergySystems.length === 0}
            >
              {t('buildingEnergyModels.drawer.addEnergySystem')}
            </Button>
          </Box>
        </Box>

        {energySystems.length > 0 ? (
          <Grid container spacing={2}>
            {energySystems.map((energySystem) => (
              <Grid item key={energySystem.id}>
                <Paper
                  className={classes.energySystem}
                  variant="elevation"
                  elevation={0}
                  tabIndex={0}
                  onClick={() => setEditingEnergySystem(energySystem)}
                >
                  <Typography variant="h5">{energySystem.name}</Typography>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      className={classes.energySystemPropertyName}
                    >
                      {t(`energySystemType.${energySystem.type}`)}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box ml={1}>
            <Typography variant="subtitle1">
              {t('energySystem.noSystems')}
            </Typography>
          </Box>
        )}
      </Box>

      {isCreatingEnergySystem && (
        <EnergySystemDialog
          buildingEnergyModelArea={buildingEnergyModel.area}
          schedules={buildingEnergyModel.schedules}
          allowedEnergySystemTypes={allowedEnergySystems}
          open={isCreatingEnergySystem}
          onClose={onDialogClose}
          onSubmit={onCreateSubmit}
          defaultValues={defaultValuesByType(allowedEnergySystems[0])}
          isCreate
        />
      )}
      {editingEnergySystem !== undefined && (
        <EnergySystemDialog
          buildingEnergyModelArea={buildingEnergyModel.area}
          schedules={buildingEnergyModel.schedules}
          allowedEnergySystemTypes={Object.values(EnergySystemType)}
          open={editingEnergySystem !== undefined}
          onClose={() => setEditingEnergySystem(undefined)}
          onSubmit={onEditSubmit}
          defaultValues={{
            name: editingEnergySystem.name,
            type: editingEnergySystem.type,
            scheduleId: editingEnergySystem.schedule?.id ?? undefined,
            parameters: convertInputParametersToFormParameters(
              editingEnergySystem.parameters,
            ),
          }}
          isCreate={false}
          onDelete={onDelete}
        />
      )}
      <LoadingDialog
        isLoading={isCreateLoading || isUpdateLoading || isDeleteLoading}
      />
    </>
  )
}
